import {
	ContractFilters,
	ContractStatusValues,
	PropositionProductTypeValues,
} from "@dune/types";
import { useCallback, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { usePaginationStore } from "../../stores/pagination.store";

export const useContractFilters = () => {
	const [searchParams, setSearchParams] = useSearchParams();
	const storedLimit = usePaginationStore((s) => s.limit);
	const setStoredLimit = usePaginationStore((s) => s.setLimit);

	const filters = useMemo((): ContractFilters => {
		const productType = searchParams.get(
			"productType",
		) as PropositionProductTypeValues | null;
		const status = searchParams.get("status") as ContractStatusValues | null;
		const constructionSiteId = searchParams.get("constructionSiteId");
		const brokerSocietyId = searchParams.get("brokerSocietyId");
		const search = searchParams.get("search");
		const page = searchParams.get("page");
		const limitParam = searchParams.get("limit");
		const sortBy = searchParams.get("sortBy");
		const sortOrder = searchParams.get("sortOrder") as "asc" | "desc" | null;

		const limit = limitParam ? Number(limitParam) : storedLimit;

		return {
			...(productType && { productType }),
			...(status && { status }),
			...(constructionSiteId && {
				constructionSiteId: Number(constructionSiteId),
			}),
			...(brokerSocietyId && { brokerSocietyId: Number(brokerSocietyId) }),
			...(search && { search }),
			...(page && { page: Number(page) }),
			limit,
			...(sortBy && { sortBy }),
			...(sortOrder && { sortOrder }),
		};
	}, [searchParams, storedLimit]);

	const updateFilters = useCallback(
		(newFilters: Partial<ContractFilters>) => {
			if (newFilters.limit != null) {
				setStoredLimit(newFilters.limit);
			}
			const params = new URLSearchParams(searchParams);

			for (const [key, value] of Object.entries(newFilters)) {
				if (value !== undefined && value !== null && value !== "") {
					params.set(key, String(value));
				} else {
					params.delete(key);
				}
			}

			setSearchParams(params);
		},
		[searchParams, setSearchParams, setStoredLimit],
	);

	const clearFilters = useCallback(() => {
		setSearchParams({});
	}, [setSearchParams]);

	return {
		filters,
		updateFilters,
		clearFilters,
		hasActiveFilters: Object.keys(filters).length > 0,
	};
};

export const useContractSelection = () => {
	const [selectedIds, setSelectedIds] = useState<number[]>([]);

	const selectContract = useCallback((id: number) => {
		setSelectedIds((prev) => [...prev, id]);
	}, []);

	const deselectContract = useCallback((id: number) => {
		setSelectedIds((prev) => prev.filter((selectedId) => selectedId !== id));
	}, []);

	const toggleContract = useCallback((id: number) => {
		setSelectedIds((prev) =>
			prev.includes(id)
				? prev.filter((selectedId) => selectedId !== id)
				: [...prev, id],
		);
	}, []);

	const selectAll = useCallback((ids: number[]) => {
		setSelectedIds(ids);
	}, []);

	const clearSelection = useCallback(() => {
		setSelectedIds([]);
	}, []);

	const isSelected = useCallback(
		(id: number) => {
			return selectedIds.includes(id);
		},
		[selectedIds],
	);

	return {
		selectedIds,
		selectContract,
		deselectContract,
		toggleContract,
		selectAll,
		clearSelection,
		isSelected,
		hasSelection: selectedIds.length > 0,
		selectionCount: selectedIds.length,
	};
};
